import React from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import CanvasRowList, { readRowListConfig } from "./CanvasRowList";

describe("readRowListConfig", () => {
  it("is off unless display is list with a primary field", () => {
    expect(readRowListConfig({})).toBeUndefined();
    expect(
      readRowListConfig({ list_config: { primary_field: "name" } }),
    ).toBeUndefined();
    expect(
      readRowListConfig({ display: "list", list_config: {} }),
    ).toBeUndefined();
  });

  it("reads snake_case and camelCase keys and drops unknown colours", () => {
    expect(
      readRowListConfig({
        display: "list",
        list_config: {
          primary_field: "name",
          secondary_field: "role",
          icon: "Person",
          status_field: "state",
          status_colors: { Open: "success", Bad: "not-a-colour" },
          chevron: true,
        },
      }),
    ).toEqual({
      icon: "Person",
      iconField: undefined,
      primaryField: "name",
      secondaryField: "role",
      statusField: "state",
      statusColors: { Open: "success" },
      chevron: true,
    });
    expect(
      readRowListConfig({
        display: "list",
        listConfig: { primaryField: "name", iconField: "glyph" },
      })?.iconField,
    ).toBe("glyph");
  });
});

describe("CanvasRowList", () => {
  const rows = [
    {
      _rowId: "r1",
      name: "Ada",
      role: "Engineer",
      state: "Open",
      glyph: "Mail",
    },
    { _rowId: "r2", name: "Bo", role: "", state: "", glyph: "" },
  ];

  it("renders icon, primary, secondary, status pill and chevron per row", () => {
    render(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{
          icon: "Person",
          primaryField: "name",
          secondaryField: "role",
          statusField: "state",
          statusColors: { Open: "success" },
          chevron: true,
        }}
      />,
    );
    expect(screen.getAllByRole("listitem")).toHaveLength(2);
    expect(screen.getByText("Ada")).toBeInTheDocument();
    expect(screen.getByText("Engineer")).toBeInTheDocument();
    expect(screen.getByTestId("status-chip")).toHaveAttribute(
      "data-status-color",
      "success",
    );
    expect(screen.getAllByTestId("row-chevron")).toHaveLength(2);
    expect(screen.getAllByTestId("PersonIcon").length).toBe(2);
  });

  it("uses the per-row icon field and falls back to the fixed icon", () => {
    render(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{ icon: "Person", iconField: "glyph", primaryField: "name" }}
      />,
    );
    expect(screen.getAllByTestId("MailIcon")).toHaveLength(1);
    expect(screen.getAllByTestId("PersonIcon")).toHaveLength(1);
  });

  it("omits optional parts that are not configured", () => {
    render(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{ primaryField: "name" }}
      />,
    );
    expect(screen.queryByTestId("status-chip")).toBeNull();
    expect(screen.queryByTestId("row-chevron")).toBeNull();
  });

  it("passes the row id and data to the row handler on click", () => {
    const onRowSelect = jest.fn();
    render(
      <CanvasRowList
        rows={rows}
        onRowSelect={onRowSelect}
        config={{ primaryField: "name" }}
      />,
    );
    fireEvent.click(screen.getByText("Bo"));
    expect(onRowSelect).toHaveBeenCalledWith("r2", rows[1]);
  });

  it("pages through every record with Previous and Next", () => {
    const onPageChange = jest.fn();
    const { rerender } = render(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{ primaryField: "name" }}
        page={0}
        pageSize={25}
        totalCount={26}
        onPageChange={onPageChange}
      />,
    );
    expect(screen.getByText("Page 1 of 2")).toBeInTheDocument();
    expect(screen.getByRole("button", { name: "Previous" })).toBeDisabled();
    fireEvent.click(screen.getByRole("button", { name: "Next" }));
    expect(onPageChange).toHaveBeenCalledWith(1);
    rerender(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{ primaryField: "name" }}
        page={1}
        pageSize={25}
        totalCount={26}
        onPageChange={onPageChange}
      />,
    );
    expect(screen.getByRole("button", { name: "Next" })).toBeDisabled();
    fireEvent.click(screen.getByRole("button", { name: "Previous" }));
    expect(onPageChange).toHaveBeenLastCalledWith(0);
  });

  it("shows no pager for a single page", () => {
    render(
      <CanvasRowList
        rows={rows}
        onRowSelect={jest.fn()}
        config={{ primaryField: "name" }}
        page={0}
        pageSize={25}
        totalCount={2}
        onPageChange={jest.fn()}
      />,
    );
    expect(screen.queryByText(/Page 1/)).toBeNull();
  });
});
